// Offline mock of the mutual-party world (ADR 0038 §2.2, substep 1). Stands in
// for the server that the next substep adds: it seeds a deterministic roster of
// synthetic partners and an inbound request queue, then simulates request →
// approval → link entirely in memory. No auth, no network, no persistence —
// established links are persisted by the active-character store onto
// `character.partyLinks`; this mock only owns the roster + request queue and the
// partners' own slot bookkeeping.
//
// Determinism: ids and timestamps come from internal counters (never Date.now /
// Math.random), so the same call sequence reproduces byte-stable state. Tests
// reset the singleton with `resetLocalPartyMock()`.

import {
  type AccountId,
  type BorrowedAllyMeta,
  type JobType,
  type PartyLink,
  type PartyMember,
  type PartyRequest,
  type PublishedSnapshotData,
} from '@renavora/shared';

import type { SerializedCharacter } from '../persistence/index.js';
import { SCHEMA_VERSION } from '../persistence/index.js';
import { buildSnapshotData } from './snapshot-data.js';
import {
  approvePartyRequest,
  createPartyRequest,
  declinePartyRequest,
  formPartyLinks,
  hasFreePartyLinkSlot,
  validatePartyLinkFormation,
  type PartyLinkFormationResult,
  type PartySlotContext,
} from './party-link.js';

export const LOCAL_PARTY_ACCOUNT_ID: AccountId = 'local-dev-account';

export class LocalPartyMockError extends Error {
  constructor(
    message: string,
    readonly reason: string,
  ) {
    super(message);
    this.name = 'LocalPartyMockError';
  }
}

/** A synthetic partner: a frozen member plus its static slot state. */
interface RosterPartner {
  member: PartyMember;
  readonly jobId: string;
  /**
   * Slots the partner already spends on *other* parties (static seed value). A
   * partner's local-to-you slot is derived from `character.partyLinks`, never
   * mutated here — so the mock holds no link state to drift if a persist fails
   * or to lose on reload (the source of the prior refresh/consistency bugs).
   */
  readonly basePreLinked: number;
  /** Snapshot version, bumped on refresh so a re-freeze yields a fresh id. */
  version: number;
}

interface PartnerSeed {
  readonly characterId: string;
  readonly name: string;
  readonly jobId: string;
  readonly level: number;
  /** Slots the partner already spends elsewhere (to exercise addressee-full). */
  readonly preLinked?: number;
}

// A small, varied, deterministic roster. Jobs span tiers so the slot cap differs
// per partner; `preLinked` lets one partner start without a free slot.
const PARTNER_SEEDS: readonly PartnerSeed[] = [
  { characterId: 'partner.aria', name: 'Aria', jobId: 'mage', level: 16 },
  { characterId: 'partner.borin', name: 'Borin', jobId: 'knight', level: 18 },
  { characterId: 'partner.cleo', name: 'Cleo', jobId: 'cleric', level: 14 },
  { characterId: 'partner.dax', name: 'Dax', jobId: 'paladin', level: 34 },
  { characterId: 'partner.evin', name: 'Evin', jobId: 'warrior', level: 8, preLinked: 1 },
];

// Synthetic partners that proactively request to join the player, seeding the
// inbound approve/decline queue.
const INCOMING_SEEDS: readonly string[] = ['partner.borin', 'partner.cleo'];

function partnerAccountId(characterId: string): AccountId {
  return `acc.${characterId}`;
}

function syntheticRecord(characterId: string, name: string, jobId: string, level: number): SerializedCharacter {
  return {
    id: characterId,
    name,
    schemaVersion: SCHEMA_VERSION,
    createdAt: 0,
    updatedAt: 0,
    character: {
      jobId,
      jobLevel: level,
      jobHistory: [{ jobId: 'warrior', maxLv: Math.max(1, Math.floor(level / 2)) }],
      equipment: [],
      inventory: [],
      currentRun: null,
      currentRunNodeCleared: null,
      gold: 0,
      xp: 0,
      completedDungeons: [],
      partyLinks: [],
    },
  };
}

/** Build a BorrowedAllyMeta for a frozen snapshot (mirrors the harness shape). */
export function snapshotMetaFor(data: PublishedSnapshotData, recommendedTier: number): BorrowedAllyMeta {
  const roleTypes: readonly JobType[] = data.currentJobTypes;
  return {
    roleTypes,
    recommendedTier,
    score: 100,
    selfRoleClaim: roleTypes[0] ?? null,
  };
}

function buildPartnerMember(seed: PartnerSeed, version: number): PartyMember {
  const data = buildSnapshotData(syntheticRecord(seed.characterId, seed.name, seed.jobId, seed.level));
  return {
    accountId: partnerAccountId(seed.characterId),
    characterId: seed.characterId,
    snapshotId: `snap.${seed.characterId}.v${version}`,
    data,
    meta: snapshotMetaFor(data, Math.max(1, Math.ceil(seed.level / 10))),
  };
}

/** Build the local player's frozen member from their active character record. */
export function localPartyMember(record: SerializedCharacter): PartyMember {
  const data = buildSnapshotData(record);
  return {
    accountId: LOCAL_PARTY_ACCOUNT_ID,
    characterId: record.id,
    snapshotId: `snap.local.${record.id}.v${record.updatedAt}`,
    data,
    meta: snapshotMetaFor(data, Math.max(1, Math.ceil(record.character.jobLevel / 10))),
  };
}

/** Local slot context for formation checks, derived from the active character. */
export function localSlotContext(record: SerializedCharacter): PartySlotContext {
  return {
    accountId: LOCAL_PARTY_ACCOUNT_ID,
    characterId: record.id,
    jobId: record.character.jobId,
    links: record.character.partyLinks,
  };
}

export interface PartyRequestView {
  readonly request: PartyRequest;
  readonly direction: 'incoming' | 'outgoing';
}

/**
 * Result of a successful request/approval: the link to persist onto the local
 * character plus the (now resolved) request for the queue view.
 */
export interface PartyLinkResult {
  readonly link: PartyLink;
  readonly request: PartyRequest;
}

class LocalPartyMock {
  private partners: RosterPartner[] = [];
  private requests: PartyRequest[] = [];
  private idCounter = 0;
  private clock = 0;

  constructor() {
    this.seed();
  }

  private seed(): void {
    this.partners = PARTNER_SEEDS.map((seed) => ({
      member: buildPartnerMember(seed, 1),
      jobId: seed.jobId,
      basePreLinked: seed.preLinked ?? 0,
      version: 1,
    }));
    this.requests = INCOMING_SEEDS.map((characterId) => {
      const partner = this.requirePartner(characterId);
      return createPartyRequest({
        id: this.nextId('req'),
        createdAt: this.tick(),
        requester: partner.member,
        addressee: this.placeholderLocalMember(),
      });
    });
  }

  private nextId(prefix: string): string {
    this.idCounter += 1;
    return `${prefix}.${this.idCounter}`;
  }

  private tick(): number {
    this.clock += 1;
    return this.clock;
  }

  /**
   * Incoming seeds are created before the local character is known, so their
   * addressee is a placeholder; the real local member is supplied at approval.
   */
  private placeholderLocalMember(): PartyMember {
    return {
      accountId: LOCAL_PARTY_ACCOUNT_ID,
      characterId: 'local',
      snapshotId: 'snap.local.pending',
      data: buildSnapshotData(syntheticRecord('local', 'You', 'warrior', 1)),
      meta: { roleTypes: [], recommendedTier: 1, score: 0, selfRoleClaim: null },
    };
  }

  private requirePartner(characterId: string): RosterPartner {
    const partner = this.partners.find((p) => p.member.characterId === characterId);
    if (partner === undefined) {
      throw new LocalPartyMockError(`Unknown partner: ${characterId}`, 'unknown_partner');
    }
    return partner;
  }

  private partnerContext(partner: RosterPartner): PartySlotContext {
    // The partner's own links are opaque to the local model; represent the
    // static pre-linked count with placeholder links so the slot check sees the
    // right occupancy. The local-to-you slot is enforced separately by the
    // duplicate-partner check (a pair can only link once), so it is not added
    // here — which keeps this purely derived from the static seed.
    const links: PartyLink[] = Array.from({ length: partner.basePreLinked }, (_unused, i) => ({
      linkId: `${partner.member.characterId}.ext.${i}`,
      linkedAt: 0,
      partner: partner.member,
    }));
    return {
      accountId: partner.member.accountId,
      characterId: partner.member.characterId,
      jobId: partner.jobId,
      links,
    };
  }

  /** Roster snapshot for the browse UI. */
  roster(): readonly PartyMember[] {
    return this.partners.map((p) => p.member);
  }

  /**
   * Whether a roster partner has an open slot for a *new* link. Derived from the
   * static pre-linked count (the local-to-you slot is gated by the duplicate
   * check, so it never affects a not-yet-linked partner).
   */
  partnerHasFreeSlot(characterId: string): boolean {
    const partner = this.requirePartner(characterId);
    return hasFreePartyLinkSlot(partner.jobId, partner.basePreLinked);
  }

  /** Pending/resolved requests split by direction relative to the local player. */
  requestViews(): readonly PartyRequestView[] {
    return this.requests.map((request) => ({
      request,
      direction: request.requester.accountId === LOCAL_PARTY_ACCOUNT_ID ? 'outgoing' : 'incoming',
    }));
  }

  private replaceRequest(next: PartyRequest): void {
    this.requests = this.requests.map((r) => (r.id === next.id ? next : r));
  }

  /**
   * Local player sends a request to a roster partner. The synthetic partner is
   * cooperative and accepts immediately (a local mock has no remote latency), so
   * a successful send yields a formed link. Throws LocalPartyMockError on an
   * invalid formation (so the store can surface the reason).
   */
  sendRequest(localMember: PartyMember, localCtx: PartySlotContext, partnerCharacterId: string): PartyLinkResult {
    const partner = this.requirePartner(partnerCharacterId);
    assertFormation(validatePartyLinkFormation(localCtx, this.partnerContext(partner)));

    const pending = createPartyRequest({
      id: this.nextId('req'),
      createdAt: this.tick(),
      requester: localMember,
      addressee: partner.member,
    });
    const approved = approvePartyRequest(pending, this.tick());
    const { requesterLink } = formPartyLinks(approved, {
      requesterLinkId: this.nextId('link'),
      addresseeLinkId: this.nextId('link'),
      linkedAt: this.tick(),
    });

    // Only the (idempotent, append-only) request queue is touched; the link
    // itself is owned by `character.partyLinks` (persisted by the store). If the
    // store's persist fails, nothing here needs rolling back.
    this.requests = [...this.requests, approved];
    return { link: requesterLink, request: approved };
  }

  /**
   * Local player approves an inbound request. Validates the local slot (双方枠:
   * requester side already had a slot when it sent) and forms the link with the
   * requester as partner — but does NOT resolve the queue entry. The store calls
   * {@link commitApproval} only after the link persists, so a persist failure
   * leaves the inbound request pending and re-approvable (no mock/save desync).
   */
  approveIncoming(requestId: string, localCtx: PartySlotContext): PartyLinkResult {
    const request = this.requireIncoming(requestId);
    const partner = this.requirePartner(request.requester.characterId);
    assertFormation(validatePartyLinkFormation(localCtx, this.partnerContext(partner)));

    const approved = approvePartyRequest(request, this.tick());
    const { addresseeLink } = formPartyLinks(approved, {
      requesterLinkId: this.nextId('link'),
      addresseeLinkId: this.nextId('link'),
      linkedAt: this.tick(),
    });

    return { link: addresseeLink, request: approved };
  }

  /**
   * Resolve a pending inbound request to `approved`. Called by the store only
   * after the corresponding link has persisted. Idempotent / no-op if the
   * request is missing or already resolved (so a re-run cannot double-apply).
   */
  commitApproval(requestId: string): void {
    const request = this.requests.find((r) => r.id === requestId);
    if (request === undefined || request.status !== 'pending') return;
    this.replaceRequest(approvePartyRequest(request, this.tick()));
  }

  /** Local player declines an inbound request. */
  declineIncoming(requestId: string): PartyRequest {
    const request = this.requireIncoming(requestId);
    const declined = declinePartyRequest(request, this.tick());
    this.replaceRequest(declined);
    return declined;
  }

  private requireIncoming(requestId: string): PartyRequest {
    const request = this.requests.find((r) => r.id === requestId);
    if (request === undefined) {
      throw new LocalPartyMockError(`Unknown request: ${requestId}`, 'unknown_request');
    }
    if (request.requester.accountId === LOCAL_PARTY_ACCOUNT_ID) {
      throw new LocalPartyMockError(`Request ${requestId} is outgoing, cannot approve.`, 'not_incoming');
    }
    return request;
  }

  /**
   * Re-freeze a partner's latest snapshot. Keyed by `partnerCharacterId` (which
   * the store reads off the persisted link), so it works after a reload when the
   * mock holds no per-link state. Bumps the partner's snapshot version so the
   * re-freeze is observably distinct.
   */
  refreshLink(partnerCharacterId: string): PartyMember {
    const partner = this.requirePartner(partnerCharacterId);
    const seed = PARTNER_SEEDS.find((s) => s.characterId === partnerCharacterId);
    if (seed === undefined) {
      throw new LocalPartyMockError(`Unknown partner seed: ${partnerCharacterId}`, 'unknown_partner');
    }
    partner.version += 1;
    partner.member = buildPartnerMember(seed, partner.version);
    return partner.member;
  }
}

function assertFormation(result: PartyLinkFormationResult): asserts result is { ok: true } {
  if (!result.ok) {
    throw new LocalPartyMockError(result.message, result.reason);
  }
}

let singleton = new LocalPartyMock();

export function getLocalPartyMock(): LocalPartyMock {
  return singleton;
}

export function resetLocalPartyMock(): LocalPartyMock {
  singleton = new LocalPartyMock();
  return singleton;
}

export type { LocalPartyMock };
